import Button from '@/components/Buttons/Button';
import Checkbox from '@/components/Checkbox/Checkbox';
import { PrivacyUrl, TermsUrl } from '@/environment';
import BottomDotsSvg from '@/images/BottomDots.svg';
import logo from '@/images/did_logo_dark.svg';
import TopDotsSvg from '@/images/TopDots.svg';
import styled from '@emotion/styled';
import { useState } from 'react';

const PageContainer = styled.div`
    width: 100vw;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${({ theme }) => theme.colors.white};
    position: relative;
    overflow: hidden;
`;

const TopDots = styled.img`
    position: fixed;
    top: 0;
    right: 0;
`;

const BottomDots = styled.img`
    position: fixed;
    bottom: 0;
    left: 0;
`;

const Modal = styled.div`
    background: ${({ theme }) => theme.colors.white};
    border-radius: 10px;
    padding: 40px;
    text-align: center;
    box-shadow: 0px 12px 40px 0px rgba(0, 0, 0, 0.12);
    width: 434px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 24px;
    position: relative;
    z-index: 1;
`;

const InnerContent = styled.div`
    width: 354px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
`;

const LogoContainer = styled.div`
    display: flex;
    justify-content: center;
    height: 47.455px;
`;

const Logo = styled.img`
    width: 58px;
    height: 47.455px;
`;

const HeadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
`;

const Heading = styled.h3`
    font-family: 'Manrope', sans-serif;
    font-size: 24px;
    font-weight: 500;
    line-height: 36px;
    margin: 0;
    color: ${({ theme }) => theme.colors.secondary.main};
    text-align: center;
    width: 347px;
`;

const Subtitle = styled.p`
    font-family: 'Manrope', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: -0.16px;
    margin: 0;
    color: ${({ theme }) => theme.colors.secondary.main};
    text-align: center;
    width: 362px;
`;

const CheckboxSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
    width: 100%;
    margin-top: -8px;
`;

const CheckboxContainer = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
    cursor: pointer;
`;

const CheckboxLabel = styled.label`
    font-family: 'Manrope', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.14px;
    color: ${({ theme }) => theme.colors.secondary.main};
    cursor: pointer;
    user-select: none;
    flex: 1;
    text-align: left;
    width: 321px;

    a {
        color: ${({ theme }) => theme.colors.links.main};
        text-decoration: underline;

        &:hover {
            text-decoration: none;
        }
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 344px;
`;

const StyledButton = styled(Button)`
    width: 342px !important;
    height: 52px !important;
    min-height: 52px !important;
    border-radius: 8px;
    font-family: 'Manrope', sans-serif;
    font-size: 16px;
    font-weight: 700;
    padding: 16.75px 127.98px 17.25px 127.02px !important;

    &.MuiButton-contained:disabled {
        background: ${({ theme }) => theme.colors.secondary[500]} !important;
        opacity: 1 !important;
    }
`;

interface TermsPageProps {
    setAccepted: () => void | Promise<void>;
}

export const TermsPage = ({ setAccepted }: TermsPageProps) => {
    const [isChecked, setIsChecked] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleCheckboxClick = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setIsChecked(prev => !prev);
    };

    const handleContinue = async () => {
        if (!isChecked || isLoading) return;

        setIsLoading(true);
        try {
            await setAccepted();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <PageContainer>
            <TopDots src={TopDotsSvg} />
            <BottomDots src={BottomDotsSvg} />
            <Modal>
                <InnerContent>
                    <LogoContainer>
                        <Logo src={logo} alt="D-ID Logo" />
                    </LogoContainer>
                    <HeadingContainer>
                        <Heading>Terms & Conditions</Heading>
                        <Subtitle>To continue, please review and accept our terms.</Subtitle>
                    </HeadingContainer>
                    <CheckboxSection>
                        <CheckboxContainer onClick={handleCheckboxClick}>
                            <Checkbox checked={isChecked} onClick={handleCheckboxClick} outlined />
                            <CheckboxLabel>
                                <span style={{ whiteSpace: 'nowrap' }}>
                                    I have read, and agree to the{' '}
                                    <a href={TermsUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                                        D-ID Terms of Service
                                    </a>
                                    ,{' '}
                                </span>
                                <br />
                                and the{' '}
                                <a href={PrivacyUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                                    Privacy Policy
                                </a>
                                .
                            </CheckboxLabel>
                        </CheckboxContainer>
                        <ButtonContainer>
                            <StyledButton
                                onClick={handleContinue}
                                disabled={!isChecked}
                                isLoading={isLoading}
                                color="secondary"
                                variant="contained">
                                Continue
                            </StyledButton>
                        </ButtonContainer>
                    </CheckboxSection>
                </InnerContent>
            </Modal>
        </PageContainer>
    );
};

export default TermsPage;
